import { useState } from "react"
import { useNavigate } from "react-router-dom"
import { CheckIcon } from "lucide-react"

import TonIcon from "@/assets/icons/ton-icon.svg?react"
import ShareIcon from "@/assets/icons/share-icon.svg?react"
import CopyIcon from "@/assets/icons/copy-icon.svg?react"

import { useReferralLink } from "@/entities/user"
import {
  PromocodeRewardDialog,
  usePromocodeActivation,
} from "@/entities/promocode"

import { TournamentRewardCard } from "@/features/tournament"

import Button from "@/shared/ui/Button"
import { useTournamentStore } from "@/shared/store"
import { useFeatureFlag } from "@/shared/features"
import { useI18n } from "@/shared/i18n"
import { telegram } from "@/shared/lib/telegram"
import { makeSuccessToast } from "@/shared/lib/toast-success"
import { AnalyticsEvent, trackAnalyticsEvent } from "@/shared/lib/analytics"

const Rewards = () => {
  const { t } = useI18n()
  const navigate = useNavigate()
  const [isCopied, setIsCopied] = useState(false)
  const [inputValue, setInputValue] = useState("")

  const tournament = useTournamentStore((s) => s.summary)
  const tournamentsEnabled = useFeatureFlag("tournaments")

  const { data: referralLink, isLoading: isReferralLinkLoading } =
    useReferralLink()

  const { activate, isPending, claimedReward, dismissReward } =
    usePromocodeActivation()

  const referralUrl = referralLink?.url ?? ""
  const referralCode = referralLink?.code ?? ""
  const invitedUsersCount = referralLink?.reffered_users_count ?? 0
  const earnedTon = (referralLink?.earned_ton_nano ?? 0) / 1e9
  const referralUrlLabel = isReferralLinkLoading
    ? t("common.loading")
    : referralCode || t("rewards.referralLinkUnavailable")

  const onClaimClick = async () => {
    const code = inputValue.trim()
    if (code.length === 0 || isPending) return
    try {
      await activate(code)
      setInputValue("")
    } catch {
      // toast handled inside the hook
    }
  }

  const onCopyClick = async () => {
    if (!referralUrl) return

    await navigator.clipboard.writeText(referralUrl)

    trackAnalyticsEvent(AnalyticsEvent.ReferralLinkCopied, {})

    makeSuccessToast({ label: t("rewards.referralLinkCopied") })

    setIsCopied(true)
    setTimeout(() => {
      setIsCopied(false)
    }, 3000)
  }

  const onShareClick = () => {
    if (!referralUrl) return

    trackAnalyticsEvent(AnalyticsEvent.ReferralShared, {})
    telegram.openTelegramLink(
      `https://t.me/share/url?url=${encodeURIComponent(referralUrl)}`
    )
  }

  return (
    <div className="min-h-full px-4 pb-10">
      <div className="flex flex-col gap-4 py-4">
        <h2 className="text-2xl/none font-semibold tracking-[-0.5px] text-white">
          {t("rewards.activityBonuses")}
        </h2>
        <label className="flex items-center gap-2 rounded-[20px] bg-secondary-main p-4 transition-all focus-within:ring-1 focus-within:ring-green-100">
          <input
            className="min-w-0 flex-1 border-none bg-transparent text-base/none font-medium tracking-[-0.5px] text-white outline-none placeholder:text-white/60"
            placeholder={t("rewards.promocodePlaceholder")}
            value={inputValue}
            onChange={(evt) => setInputValue(evt.currentTarget.value)}
          />
          <button
            type="button"
            className="flex h-10 shrink-0 items-center justify-center rounded-full bg-white px-3 text-black-100 transition-all disabled:bg-white/30"
            disabled={inputValue.trim().length === 0 || isPending}
            onClick={onClaimClick}
          >
            {t("rewards.claim")}
          </button>
        </label>
      </div>

      {tournamentsEnabled && tournament && (
        <div className="flex flex-col gap-4 py-4">
          <h2 className="text-2xl/none font-semibold tracking-[-0.5px] text-white">
            {t("tournamentRewards.sectionTitle")}
          </h2>
          <TournamentRewardCard
            tournament={tournament}
            onViewLeaderboard={() => {
              trackAnalyticsEvent(AnalyticsEvent.TournamentLeaderboardOpen, {
                source: "rewards_card",
              })
              navigate("/tournament-leaderboard")
            }}
          />
        </div>
      )}

      <div className="flex flex-col gap-4 py-4">
        <h2 className="text-2xl/none font-semibold tracking-[-0.5px] text-white">
          {t("rewards.inviteFriends")}
        </h2>
        <div className="flex flex-col gap-2">
          <div className="flex gap-2">
            <div className="flex flex-1 flex-col gap-3 rounded-[20px] bg-secondary-main p-4 font-semibold tracking-[-0.5px] text-white">
              <span className="text-lg/none">{t("rewards.invited")}</span>
              <span className="text-2xl/none">{invitedUsersCount}</span>
            </div>
            <div className="flex flex-1 flex-col gap-3 rounded-[20px] bg-secondary-main p-4 font-semibold tracking-[-0.5px] text-white">
              <span className="text-lg/none">{t("rewards.totalEarned")}</span>
              <span className="flex items-center gap-1 text-2xl/none">
                {earnedTon.toFixed(2)} <TonIcon className="size-6" />
              </span>
            </div>
          </div>
          <div className="flex flex-1 flex-col gap-2 rounded-[20px] bg-secondary-main p-4 font-semibold tracking-[-0.5px] text-white">
            <span className="text-lg/none">{t("rewards.referralProgram")}</span>
            <span className="text-sm/none font-normal text-white/60">
              {t("rewards.referralProgramHint")}
            </span>
            <div className="mt-2 flex h-12 items-center gap-2">
              <button
                className="flex h-full flex-1 items-center justify-between rounded-full bg-white/[6%] px-4 disabled:opacity-60"
                onClick={onCopyClick}
                disabled={!referralUrl}
              >
                <span className="max-w-[200px] truncate text-sm/none font-normal">
                  {referralUrlLabel}
                </span>
                {isCopied ? (
                  <CheckIcon className="size-4 text-white/60 animate-in fade-in-0" />
                ) : (
                  <CopyIcon className="size-4 text-white/60 animate-in fade-in-0" />
                )}
              </button>
              <button
                className="flex aspect-square h-full shrink-0 items-center justify-center rounded-full bg-white transition-colors active:bg-white/60 disabled:pointer-events-none disabled:opacity-60"
                onClick={onShareClick}
                disabled={!referralUrl}
              >
                <ShareIcon className="size-5 text-black-100" />
              </button>
            </div>
          </div>
        </div>
      </div>

      <div className="flex flex-col rounded-[20px] bg-secondary-main p-4">
        <span className="text-lg/none font-semibold tracking-[-0.5px] text-white">
          {t("rewards.partnerTitle")}
        </span>
        <p className="mb-4 mt-2 text-sm/none tracking-[-0.5px] text-white/60">
          {t("rewards.partnerDescription")}
        </p>
        <Button
          appearance="green"
          size="small"
          type="button"
          onClick={() => {
            trackAnalyticsEvent(AnalyticsEvent.PartnerCtaClick, {
              destination: "telegram_bot",
            })
            telegram.openTelegramLink("https://t.me/askTRiX_bot")
          }}
        >
          {t("rewards.becomeAffiliate")}
        </Button>
      </div>

      <PromocodeRewardDialog
        open={!!claimedReward}
        onOpenChange={(open) => {
          if (!open) dismissReward()
        }}
        imageUrl={claimedReward?.imageUrl ?? ""}
        lottieUrl={claimedReward?.lottieUrl ?? null}
        giftName={claimedReward?.giftName ?? ""}
        amount={claimedReward?.amountTon ?? 0}
        wagerRequirement={claimedReward?.wagerRequirement ?? undefined}
      />
    </div>
  )
}

export default Rewards
